import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { InAppTemplate } from "../../../shared/templates/inapp";
import PostEditor from "./components/PostEditor";
import {
  SuccessModal,
  ConfirmationModal,
  ErrorModal,
} from "../../../shared/components/modals";
import { BlogService } from "../../../services/blogService";
import {
  adaptBlogPostFromApi,
  adaptBlogPostToUpdateCommand,
  UIBlogPost,
} from "../../../services/blogAdapters";
import "./blog.scss";
import "./blog-editor.scss";
import { LoadingItem } from "../../../shared/components/loading";

type BlogPost = UIBlogPost;

const EditBlogPost = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [pendingSaveIsDraft, setPendingSaveIsDraft] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const loadPost = useCallback(async () => {
    if (!slug) {
      setShowErrorModal(true);
      return;
    }

    try {
      setLoading(true);
      const apiPost = await BlogService.getBlogPostBySlug(slug);
      const adaptedPost = adaptBlogPostFromApi(apiPost, "published");
      setPost(adaptedPost);
    } catch (error) {
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadPost();
  }, [loadPost]);

  const handleSaveRequest = (
    updatedPost: Partial<BlogPost>,
    isDraft: boolean,
  ) => {
    setPost({ ...post, ...updatedPost } as any);
    setPendingSaveIsDraft(isDraft);
    setShowSaveConfirmModal(true);
  };

  const handleConfirmSave = async () => {
    if (!slug || !post) return;

    try {
      setSaving(true);
      setShowSaveConfirmModal(false);

      const postToSave: UIBlogPost = {
        ...post,
        status: pendingSaveIsDraft ? "draft" : "published",
      };

      const updateCommand = adaptBlogPostToUpdateCommand(postToSave);
      await BlogService.updateBlogPost(post.id, updateCommand);

      await loadPost();

      setSuccessMessage(
        `Post ${
          pendingSaveIsDraft ? "saved as draft" : "published"
        } successfully!`,
      );
      setShowSuccessModal(true);
      setHasUnsavedChanges(false); // Reset unsaved changes after successful save
    } catch (error) {
      setErrorMessage("Failed to update post. Please try again.");
      setShowErrorModal(true);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowConfirmModal(true);
    } else {
      navigate("/app/blog");
    }
  };

  const handlePostChange = () => {
    setHasUnsavedChanges(true);
  };

  const handleConfirmDiscard = () => {
    navigate("/app/blog");
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
  };

  const handleErrorModalClose = () => {
    setShowErrorModal(false);
    navigate("/app/blog");
  };

  if (!post) {
    return (
      <InAppTemplate
        pageHeadingTitle="Edit Post"
        childComponent={
          <div className="blog-editor-page">
            <LoadingItem />
          </div>
        }
      />
    );
  }

  return (
    <>
      <InAppTemplate
        pageHeadingTitle="Edit Post"
        childComponent={
          <div className="blog-editor-page">
            <PostEditor
              post={post}
              onSave={handleSaveRequest}
              onCancel={handleCancel}
              onChange={handlePostChange}
              isNew={false}
            />
          </div>
        }
      />

      <SuccessModal
        show={showSuccessModal}
        onHide={handleSuccessModalClose}
        title="Success"
        message={successMessage}
        actionText="Continue"
        onAction={handleSuccessModalClose}
      />

      <ConfirmationModal
        show={showSaveConfirmModal}
        onHide={() => setShowSaveConfirmModal(false)}
        title={pendingSaveIsDraft ? "Save as Draft?" : "Publish Post?"}
        message={
          pendingSaveIsDraft
            ? "Are you sure you want to save this post as a draft?"
            : "Are you sure you want to publish this post? It will be visible to all users."
        }
        confirmText={pendingSaveIsDraft ? "Save Draft" : "Publish"}
        cancelText="Cancel"
        onConfirm={handleConfirmSave}
        variant="primary"
      />

      <ConfirmationModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        title="Discard Changes"
        message="Are you sure you want to discard your changes?"
        confirmText="Discard"
        cancelText="Keep Editing"
        onConfirm={handleConfirmDiscard}
        variant="danger"
      />

      <SuccessModal
        show={showErrorModal}
        onHide={handleErrorModalClose}
        title="Post Not Found"
        message="The blog post you're trying to edit was not found."
        autoClose={false}
      />

      <ErrorModal
        show={errorMessage !== ""}
        onHide={() => setErrorMessage("")}
        title="Error"
        message={errorMessage}
        actionText="OK"
      />
    </>
  );
};

export default EditBlogPost;
